import { MessageCircle } from 'lucide-react'
import { useEffect, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import { dismissConfirmation, getConfirmSnapshot, subscribeConfirm } from '../../../stores/assistant-confirm-store'
import { getAssistantSnapshot, setAssistantOpen, subscribeAssistant } from '../../../stores/assistant-store'
import { ASSISTANT_LAYER_CLASS } from './assistant-layer'
import { BUBBLE_ANCHOR_KEY, bubbleDefaultAnchor, bubbleSizeFor } from './assistant-placement'
import { AssistantPanel } from './AssistantPanel'
import { useEdgeAnchoredDrag } from './useEdgeAnchoredDrag'

/**
 * The sitewide assistant's VISIBLE half — mounted once in `AppLayout`, in the
 * row it shares with `<main>`, AFTER `<main>`, so the dock can sit beside the
 * page and take room from it. Every form renders in that one slot. The
 * launcher and the floating window are `fixed`, so the slot does not move
 * them, but coming after `<main>` means they win any z-index tie with the
 * header's and the page's overlays. Their layer is chosen so there is no tie;
 * see `assistant-layer.ts`.
 *
 * `bubble` and `dock` are two presentation FORMS of ONE conversation, never
 * two conversations: the machine is interpreted exactly once, in
 * `AssistantProvider` (mounted above `<Routes>`), and `AssistantPanel` is a
 * pure consumer of that context via `useAssistant()`. Toggling `form` only
 * changes which JSX branch that panel renders — it never remounts, re-keys, or
 * otherwise disturbs the provider, so `state.context.sessionId` and
 * `state.context.messages` are trivially identical across the toggle.
 *
 * SELF-GATING (why this is split into an outer store-only shell and an inner
 * context-consuming panel — now `./AssistantPanel.tsx`, moved out when the
 * confirmation card took this file past the 300-line rule):
 * `AppLayout.liveChannel.spec.tsx` and
 * `DashboardView.spec.tsx`'s authenticated-chrome test both render the real
 * `<AppLayout>` with NO `<AssistantProvider>` above it — the provider is
 * mounted in `routes/index.tsx`, outside either test's tree. `useAssistant()`
 * throws outside a provider. So the OUTER component (`AssistantSurface`)
 * reads only `assistant-store` (no context, cannot throw) and — while the
 * assistant is closed, which is the default state both specs exercise —
 * renders just the launcher button. The context-consuming `AssistantPanel`
 * only mounts once `open` is true, which neither spec ever triggers. This is
 * also the correct production shape, not merely a test workaround: the
 * launcher is a persistent, always-reachable affordance (every authenticated
 * route gets a floating way to reach the assistant); it needs none of the
 * conversation state to render, so it never depends on the throwing hook.
 */
export function AssistantSurface() {
  const { open, form } = useSyncExternalStore(subscribeAssistant, getAssistantSnapshot)
  const { pending } = useSyncExternalStore(subscribeConfirm, getConfirmSnapshot)

  // A question the user can no longer SEE is a run suspended forever: the
  // browser never sends its tool result and the turn never resumes. Closing the
  // panel therefore answers NO — visible to the model, and safe, because a
  // declined confirmation writes nothing. (Hooks run before the early return
  // below, which is why this lives in the outer component: the panel that
  // renders the card is unmounted by exactly the event being handled.)
  useEffect(() => {
    if (!open && pending) dismissConfirmation()
  }, [open, pending])

  if (!open) return <AssistantLauncher />

  return <AssistantPanel form={form} />
}

/** Eases the snap after a drop (and the hover scale); off while dragging, so it tracks the pointer exactly. */
const LAUNCHER_SETTLE_CLASS = 'transition-[left,top,transform] duration-150 ease-out motion-reduce:transition-none'

function AssistantLauncher() {
  const { t } = useTranslation('console')
  const { point, dragging, onPointerDown, wasDragged } = useEdgeAnchoredDrag({
    storageKey: BUBBLE_ANCHOR_KEY,
    sizeFor: bubbleSizeFor,
    defaultAnchor: bubbleDefaultAnchor,
  })

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onClick={() => {
        if (wasDragged()) return
        setAssistantOpen(true)
      }}
      aria-label={t('assistant.launcher.open', 'Open assistant')}
      title={t('assistant.launcher.open', 'Open assistant')}
      data-testid="assistant-launcher"
      style={{ left: point.x, top: point.y, touchAction: 'none' }}
      className={`fixed ${ASSISTANT_LAYER_CLASS} flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:scale-105 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 ${
        dragging ? 'cursor-grabbing' : LAUNCHER_SETTLE_CLASS
      }`}
    >
      <MessageCircle className="h-6 w-6" aria-hidden="true" />
    </button>
  )
}
